import { Environment } from './environment.js';
import { allEnumValues, isValidEnumValue } from './helper.js';

// We use an abstract class here instead of just exporting functions because we have some tests
// that use sinon to fake isDevOrTest.
export abstract class EnvironmentHelper {
  public static current(): Environment {
    // Unfortunately this method is too common for me to just restrict it and refactor half of the
    // codebase. However, as long as it actually throws if the env var is not defined, it's safe.
    // oxlint-disable-next-line no-restricted-syntax
    const env = process.env.APP_ENV as Environment;
    if (!env) {
      throw new Error('APP_ENV is not set!!!');
    }
    if (!isValidEnumValue<Environment>(Environment, env)) {
      throw new Error(`Invalid APP_ENV set: ${env}`);
    }
    return env;
  }

  public static isCurrentDev(env: Environment = this.current()): boolean {
    return [Environment.DEV].includes(env);
  }

  public static isCurrentTest(env: Environment = this.current()): boolean {
    return env === Environment.TEST;
  }

  public static isCurrentProd(env: Environment = this.current()): boolean {
    return env === Environment.PROD;
  }

  public static isCurrentProdOrStaging(env: Environment = this.current()): boolean {
    return env === Environment.PROD || env === Environment.STAGING;
  }

  public static isCurrentStagingOrSandbox(env: Environment = this.current()): boolean {
    return env === Environment.STAGING || env === Environment.SANDBOX;
  }

  public static isHosting(env: Environment): boolean {
    return !this.isDevOrTest(env);
  }

  public static isDevOrTest(env: Environment): boolean {
    switch (env) {
      case Environment.PROD:
      case Environment.STAGING:
      case Environment.SANDBOX:
        return false;
      case Environment.DEV:
      case Environment.TEST:
        return true;
    }
  }

  public static hostingEnvironments(): Environment[] {
    return allEnumValues<Environment>(Environment).filter((env) => {
      return !this.isDevOrTest(env);
    });
  }

  /**
   * Returns the Porter preview PR number if available.
   *
   * This is intentionally written to work in BOTH:
   * - Node/server environments (where `process.env.PORTER_PR_NUMBER` may be set)
   * - Browser environments (where our bundlers may not polyfill `process`, but we may inject
   *   `__PORTER_PR_NUMBER__` onto `globalThis`, or infer it from the hostname: `pr-<num>-...`).
   *
   * If no PR number is detected, returns an empty string.
   */
  public static porterPrNumber(): string {
    const globals: any = typeof globalThis !== 'undefined' ? globalThis : {};

    // Node-ish
    const prFromProcess = globals?.process?.env?.PORTER_PR_NUMBER;
    if (typeof prFromProcess === 'string') {
      const pr = prFromProcess.trim();
      if (pr.length > 0) {
        return pr;
      }
    }

    // Browser-injected
    const prFromGlobal = globals?.['__PORTER_PR_NUMBER__'];
    if (typeof prFromGlobal === 'string') {
      const pr = prFromGlobal.trim();
      if (pr.length > 0) {
        return pr;
      }
    }

    return '';
  }

  public static isPreview(): boolean {
    return this.porterPrNumber().trim().length > 0;
  }
}
